// @refresh reset — dev-only: force remount so board nodes hot-reload on edit
import { lazy, Suspense, useEffect, useMemo, useState, createContext, useContext, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { type Node, type NodeTypes } from "@xyflow/react";
import { Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";
import { TicketsKanbanPage } from "../tickets/TicketsKanbanPage";
import { PlanningVariations } from "./PlanningVariations";
import { AgentBrainApp } from "./AgentBrainApp";
import { KnowledgeFeedBoard } from "./KnowledgeFeedBoard";
import { SkillsApp } from "./SkillsApp";
import { OpenSourceApp } from "./OpenSourceApp";
import { CoursesApp } from "./CoursesApp";
import { ToolsApp } from "./ToolsApp";
import { LeadsKanban } from "./LeadsKanban";
import { ProjectsKanban } from "./ProjectsKanban";
import { AirDuctPricingSheet } from "./airduct/calculator";
import { Everything420PricingSheet } from "./everything420/pricing";
import { AirDuctProposal, AirDuctPhaseOneProposal, Everything420Proposal } from "./command/proposal";
import { C4Diagram } from "./command/c4";
import { HydrationDiagram } from "./command/hydration";
import { TheMoveDiagram } from "./command/the-move";
import { AnimeLab } from "./command/anime-lab";
import { NotebookFrame } from "./NotebookFrame";
import { DocumentFrame } from "./DocumentFrame";
import { PresentationFrame } from "./PresentationFrame";

import { OutformArchitectureDiagram } from "./command/outform-architecture";
import { OutformErd } from "./command/outform-erd";
import { Entity261152 } from "./command/outform-entity-261152";
import { Job261152Diagram } from "./command/outform-job-anatomy";
import { OutformWorkbookAnatomy } from "./command/outform-workbook-anatomy";
import { OutformElevenDiagram } from "./command/outform-eleven";
import { DataProgrammeDiagram } from "./command/data-programme";
// ⛔ LAZY, AND THIS IS NOT A STYLE CHOICE — I broke the build by getting it
// wrong. Univer is ~5.5 MB of grid + formula engine, and importing it eagerly
// OOM-kills `vite build` at "rendering chunks" on this box. OutformBoardPage
// has carried a comment saying exactly that since 20 Aug; I added this section
// on 25 Aug with a static import anyway, and EVERY Cloudflare deploy failed
// silently from that moment until 28 Aug — heap exhaustion, exit 134. Nobody
// noticed because /dash/ is a live Vite server, so the board looked fine while
// the deployed site sat three days stale and every new /api route 404'd.
// As its own chunk it loads when someone scrolls to it, which is also just
// correct: a section nobody has opened has no business in the first byte.
const MatanUniverSheet = lazy(() =>
  import("./company/matan-univer").then((m) => ({ default: m.MatanUniverSheet })),
);
import { AgentRoom } from "./room/AgentRoom";

// Their blueprint PDF + its 24 page images, the same R2 prefix the Outform
// board uses. One asset, two boards — never a second copy.
const BLUEPRINT_R2 =
  "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/outform/blueprint";


// ─────────────────────────────────────────────────────────────────────────────
// THE JARVIS BOARD — Jarvis's command surface. Sections (Erez, 2026-07-24):
//   • Planning — 2-Week / Month / 3-Month views.
//   • Projects — every division as a clean card (Jarvis's own "advance
//                everything" project included), with its Now/Next plan and any
//                decision that needs Erez folded IN (not a detached list).
//   • Video Lab — Jarvis's code-generated video briefings.
//   • Memory   — the agent-centric brain app (sidebar of agents → summary /
//                project / logs + company brain).
// Projects are DATA-DRIVEN from command_state (cheap write, no deploy).
// Design law = editorial / landing-page register (skill://design): light,
// elegant, instant comprehension.
// ─────────────────────────────────────────────────────────────────────────────

const T = {
  bg: "#E7E5E1", dots: "#D2CFC9",
  ink: "#1B211E", ink2: "#4A534D", faint: "#8C948E",
  line: "#ECE7DF", hair: "#E3DED4",
  card: "#FFFFFF", paper: "#FAF7F2", bone: "#F2EDE4",
  green: "#3F8F6F", greenDeep: "#2E6B52", greenWash: "#EEF5F1",
  plum: "#7A5B78", amber: "#B07A1E", red: "#C0492F", redWash: "#F7EBE8",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const EYEBROW: React.CSSProperties = {
  fontFamily: MONO, fontSize: 11.5, fontWeight: 600, letterSpacing: "0.16em",
  textTransform: "uppercase", color: T.faint,
};
const CARD = (extra: React.CSSProperties = {}): React.CSSProperties => ({
  boxSizing: "border-box", background: T.card, border: `1px solid ${T.hair}`,
  borderRadius: 20, boxShadow: "0 10px 30px rgba(27,33,30,0.05)", fontFamily: FONT, ...extra,
});
const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

// ── data shapes ──────────────────────────────────────────────────────────────
type Decision = { title: string; context: string; recommendation: string; owner?: string };
type AgendaItem = { time?: string; title: string; detail?: string; done?: boolean };
type AgentPlan = { agent: string; role?: string; today?: string; next?: string };
type CommandState = {
  briefing_headline: string | null;
  briefing_message: string | null;
  decisions: Decision[];
  agenda: AgendaItem[];
  agent_plans: AgentPlan[];
  day_date: string | null;
  updated_at: string;
};
type Project = { slug: string; name: string; status: string; owner_agent: string | null; current_focus: string | null; board_order: number | null };

const BOARD_ROUTE: Record<string, string> = {
  jarvis: "/command",
  leo: "/marketing-board", ben: "/command", sol: "/startup-board",
  remy: "/youtube-board", emma: "/max-security-board", cleo: "/elhadegel-board",
  kai: "/elad-engineering-board", milo: "/boiling-offers-board", nova: "/flameking-manager",
  atlas: "/board", dave: "/knowledge-board",
};
const STATUS: Record<string, { dot: string; label: string }> = {
  active: { dot: "#3F8F6F", label: "Active" }, running: { dot: "#3F8F6F", label: "Running" },
  waiting: { dot: "#B07A1E", label: "Waiting" }, blocked: { dot: "#C0492F", label: "Blocked" },
  paused: { dot: "#8C948E", label: "Paused" }, done: { dot: "#8C948E", label: "Done" },
};

// Projects is now a working KANBAN with three switchable lenses rather than a
// flat 4-across card grid (Erez, 2026-07-25). The grid showed all 13 projects at
// equal weight, so nothing said which of them needed him. See ProjectsKanban for
// why three views and why the drag does not persist yet.
function Projects({ projects, plans, decisions }: { projects: Project[]; plans: AgentPlan[]; decisions: Decision[] }) {
  return (
    <SectionBox title="The project kanban" layout="custom">
      <div style={{ marginTop: 0 }}>
        <ProjectsKanban projects={projects} plans={plans} decisions={decisions} />
      </div>
    </SectionBox>
  );
}

// ── Video Lab — Jarvis's code-generated video briefings ──────────────────────
const VIDEOLAB_BASE = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/videolab";
const LAB_VIDEOS: { key: string; title: string; sub: string; method: string; tone: string }[] = [
  { key: "ffmpeg-hello", title: "Hey, how's it going?", sub: "made with FFmpeg", method: "FFMPEG", tone: T.green },
  { key: "remotion-hello", title: "Hey, how's it going?", sub: "rendered with Remotion", method: "REMOTION", tone: T.plum },
  { key: "process-video", title: "How it's made", sub: "the process, start to finish", method: "FFMPEG · 30s", tone: T.amber },
];

// Modal rendered at page root via portal — OUTSIDE the React Flow viewport
// transform (a transformed ancestor breaks position:fixed).
function VideoModal({ playKey, onClose }: { playKey: string; onClose: () => void }) {
  return createPortal(
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 99999, background: "rgba(20,17,15,0.84)", display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "min(1100px, 94%)", borderRadius: 18, overflow: "hidden", background: "#000", boxShadow: "0 40px 120px rgba(0,0,0,0.5)" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 12, right: 12, zIndex: 10, cursor: "pointer", width: 38, height: 38, borderRadius: "50%", border: "none", background: "rgba(28,25,23,0.7)", color: "#fff", fontSize: 22 }}>×</button>
        <video src={`${VIDEOLAB_BASE}/${playKey}.mp4`} controls autoPlay style={{ display: "block", width: "100%", maxHeight: "85vh", background: "#000" }} />
      </div>
    </div>,
    document.body,
  );
}

function VideoLab({ onPlay }: { onPlay: (key: string) => void }) {
  return (
    <SectionBox title="Video Lab" layout="custom">
      <div style={{ display: "flex", gap: 26 }}>
        {LAB_VIDEOS.map((v) => (
          <div
            key={v.key}
            className="nopan nodrag"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onPlay(v.key)}
            style={CARD({ width: 480, padding: 0, cursor: "pointer", overflow: "hidden" })}
          >
            <div style={{ height: 270, background: "#141B18", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18, position: "relative" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 5, background: v.tone }} />
              <div style={{ width: 66, height: 66, borderRadius: 999, background: "rgba(255,255,255,0.12)", border: "1.5px solid rgba(255,255,255,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: 0, height: 0, borderTop: "13px solid transparent", borderBottom: "13px solid transparent", borderLeft: "22px solid #fff", marginLeft: 6 }} />
              </div>
              <div style={{ color: "#fff", fontSize: 26, fontWeight: 600, letterSpacing: "-0.01em" }}>{v.title}</div>
              <div style={{ color: "#5FBFA0", fontSize: 15 }}>{v.sub}</div>
            </div>
            <div style={{ padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ ...EYEBROW, fontSize: 11 }}>Code-generated</span>
              <span style={{ fontFamily: MONO, fontSize: 11.5, fontWeight: 700, color: v.tone, background: T.bone, borderRadius: 999, padding: "4px 12px" }}>{v.method}</span>
            </div>
          </div>
        ))}
      </div>
    </SectionBox>
  );
}

// synthetic Jarvis project — his "advance everything" project, first in the grid
const JARVIS_PROJECT: Project = {
  slug: "__jarvis__", name: "Orchestration", status: "active", owner_agent: "jarvis",
  current_focus: "Advance every division; unblock what's stuck; keep the board the single source of truth.", board_order: -1,
};


// ══ SECTION VISIBILITY ══════════════════════════════════════════════════════
// Erez, 31 Aug 2026: "add the side menu — everything is turned off and only the
// first row is on." The board had grown to 21 sections and opened with all of
// them, which is why it read as noise. Every section is now a named, toggleable
// entry; the OUTFORM group is the first row and the only one on by default.
// The registry is the single source of truth — a section not listed here is
// still rendered (fail-open), so adding one never makes it silently vanish.
const DOCLBL = { font: `700 21px ${FONT}`, opacity: .55, marginBottom: 12, letterSpacing: ".01em" } as const;
type SecGroup = "Outform" | "R&D" | "Apps" | "Business";
const SECTION_REGISTRY: { n: string; g: SecGroup }[] = [
  { n: "Action items", g: "Apps" },
  { n: "The input forms", g: "Outform" },
  { n: "The product", g: "Outform" },
  { n: "The source documents", g: "Outform" },
  { n: "The database today", g: "Outform" },
  { n: "The entity diagrams", g: "Outform" },
  { n: "Brief for Scott", g: "Outform" },
  { n: "The system diagram", g: "R&D" }, { n: "The code architecture", g: "R&D" },
  { n: "Where logic lives", g: "R&D" }, { n: "The agent room", g: "R&D" },
  { n: "Three planning views", g: "R&D" }, { n: "Local versus server", g: "R&D" },
  { n: "The diagram standard", g: "R&D" },
  { n: "The agent brain", g: "Apps" }, { n: "The living skills", g: "Apps" }, { n: "Open source directory", g: "Apps" },
  { n: "The course library", g: "Apps" }, { n: "Commercial tools reviewed", g: "Apps" },
  { n: "The animation lab", g: "Apps" },
  { n: "Everything we watch", g: "Apps" },
  { n: "The project kanban", g: "Apps" }, { n: "The video lab", g: "Apps" },
  { n: "The lead pipeline", g: "Business" }, { n: "Matan partnership forecast", g: "Business" },
  { n: "One rate card", g: "Business" },
  { n: "The work proposals", g: "Business" },
];
// ⛔ "Action items" is forced ON alongside the Outform group. It is in the
// Apps group by nature, and the default only enables Outform — so simply
// registering it would have hidden the one section whose entire purpose is
// that Erez can read my state WITHOUT asking me. A default that hides the
// status board is worse than no status board, because it looks present.
const DEFAULT_ON = new Set([
  "Action items",
  ...SECTION_REGISTRY.filter((r) => r.g === "Outform").map((r) => r.n),
]);

const VisCtx = createContext<Set<string> | null>(null);
function Sec({ n, children }: { n: string; children: ReactNode }) {
  const vis = useContext(VisCtx);
  // fail-open: an unregistered section always renders rather than disappearing
  if (vis && SECTION_REGISTRY.some((r) => r.n === n) && !vis.has(n)) return null;
  return <>{children}</>;
}

function SectionMenu({ vis, setVis }: { vis: Set<string>; setVis: (s: Set<string>) => void }) {
  // Erez, 31 Aug 2026: "make the menu part of the board — so I can zoom in and
  // out. It needs to be small, next to the section, maximum the height of the
  // section." So it is a normal flex child of the SectionGrid, NOT a fixed
  // overlay: it scales with the canvas like everything else, and it sits at the
  // start of the first row beside the section it controls.
  const toggle = (n: string) => { const c = new Set(vis); c.has(n) ? c.delete(n) : c.add(n); setVis(c); };
  const setGroup = (g: SecGroup, on: boolean) => {
    const c = new Set(vis);
    SECTION_REGISTRY.filter((r) => r.g === g).forEach((r) => (on ? c.add(r.n) : c.delete(r.n)));
    setVis(c);
  };
  const groups: SecGroup[] = ["Outform", "R&D", "Apps", "Business"];
  const btn = { font: `600 20px ${FONT}`, padding: "8px 0", border: "1px solid #B4B4B4", borderRadius: 8, background: "#E4E4E4", cursor: "pointer", flex: 1 } as const;
  return (
    <div className="nopan nodrag" style={{ display: "inline-block", flexShrink: 0, alignSelf: "flex-start" }}>
      <div style={{ width: 460, maxHeight: 1500, overflowY: "auto", background: "#D2D2D2", border: "1px solid #BEBEBE", borderRadius: 14, padding: "22px 24px 24px", font: `${FONT}` }}>
        <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: ".10em", textTransform: "uppercase", opacity: .5, marginBottom: 14 }}>Sections</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          <button style={btn} onClick={() => setVis(new Set(SECTION_REGISTRY.map((r) => r.n)))}>All</button>
          <button style={btn} onClick={() => setVis(new Set(DEFAULT_ON))}>Reset</button>
          <button style={btn} onClick={() => setVis(new Set())}>None</button>
        </div>
        {groups.map((g) => {
          const rows = SECTION_REGISTRY.filter((r) => r.g === g);
          const on = rows.filter((r) => vis.has(r.n)).length;
          return (
            <div key={g} style={{ marginBottom: 16 }}>
              <div
                onClick={() => setGroup(g, on !== rows.length)}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", cursor: "pointer", padding: "6px 0 8px", borderBottom: "1px solid #B8B8B8", marginBottom: 6 }}
              >
                <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", opacity: .55 }}>{g}</span>
                <span style={{ fontSize: 16, opacity: .45 }}>{on}/{rows.length}</span>
              </div>
              {rows.map((r) => (
                <label key={r.n} style={{ display: "flex", gap: 11, alignItems: "center", padding: "5px 0", cursor: "pointer" }}>
                  <input type="checkbox" checked={vis.has(r.n)} onChange={() => toggle(r.n)} style={{ width: 19, height: 19, flexShrink: 0, accentColor: "#2A6AAC", cursor: "pointer" }} />
                  <span style={{ fontSize: 21, opacity: vis.has(r.n) ? 1 : .42, whiteSpace: "nowrap" }}>{r.n}</span>
                </label>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GridNode({ data }: { data: { cs: CommandState; projects: Project[]; onPlay: (key: string) => void; vis: Set<string>; setVis: (s: Set<string>) => void } }) {
  const { cs, projects, onPlay, vis, setVis } = data;
  return (
    <VisCtx.Provider value={vis}>
    <SectionGrid width={7000} gap={170}>
      {/* ⭐⭐ FIRST SECTION, and it is first for a reason. Erez, 1 Sept 2026,
          after a morning of asking me what state things were in: "bring an
          action item mini app into the top of your board, and let's start
          managing tickets with screenshots using the tickets."

          Every other agent's board is where he reviews their work. Mine had
          diagrams and documents and no list of what I am DOING, so the only
          way to learn the state of anything was to ask me — and my answers
          were prose, from memory, and twice that morning they were wrong.

          ⛔ A TEST CASE IS PROVEN BY A SCREENSHOT, NOT BY A 200. The card
          counts the images in each ticket and says so on its face, so a test
          ticket carrying none is visibly unproven — which is precisely the
          state I had been reporting as "passing". */}
      <Sec n={'Action items'}>
      <SectionBox title="Action items" layout="custom">
        <div style={{ marginTop: 0 }}>
          <TicketsKanbanPage project="consultancy" embedded />
        </div>
      </SectionBox>
      </Sec>

      {/* ⭐ TOP SECTION, at Erez's instruction 31 Aug 2026 — he is meeting SCOTT,
          Outform's main estimator, to review the Excel with him, and asked for
          the eleven job folders distilled to one short page he can walk in with:
          "what structure each represents, if it belongs to a family, what
          questions we have about the business logic, and if we want one input
          form — what MUST be there from all the jobs."
          Built from the eleven job dossiers under kb-doc/consultancy/outform/jobs/*
          plus the family analysis. Four tables: the four-engine map · one row per
          job with its single question for Scott · ten business-logic questions ·
          the derived form spec.
          ⛔ IT ALSO SITS ON /outform-board, which is QUINN'S file — she placed it
          there (c834404) and then audited this page against the client database
          rather than against her own job pages, which is what caught the LG
          ladder arithmetic and the 261801 tense. Two frames, one page_content
          row: no copy to drift. */}
      {/* The artefact of the 31 Aug reasoning run — eight cycles, five of which
          killed or reshaped the claim they started with. First on the board
          because it is the only section that says what we are DOING. */}
      {/* ⛔ THE WORK BOARD IS GONE, on Erez's word (2 Sept): "what Atlas is
          doing and what is waiting — delete this thing, I don't know why it's
          here." It was added the same day on a reading of "you manage the work"
          that meant tracking, and he wants his board to be the PRODUCT, not a
          status feed about an agent. The page itself is untouched at
          `kb-doc/consultancy/outform/product/28-the-work-board`; only the frame
          on this board is removed. */}

      {/* ══ THE TWO INPUT FORMS, DESIGNED ═══════════════════════════════════
          Erez, 1 Sept 2026, pointing at https://ui.shadcn.com/: "this is how I
          want to design our input pages with these components from shadcn with
          different boxes. It should not be just like a flat out form. It should
          be like boxes in different sizes, more wide, more tall, and really kind
          of build it up... create a section next to the strategy and just create
          the UI for the ballpark and for the details."

          NEXT TO THE STRATEGY deliberately — the strategy says what we are
          doing and this says what it looks like when it is done. They are the
          same conversation.

          ⛔ NOT A THIRD BOARD COMPONENT TYPE. Board law says a section is a
          presented KB page, a presentation, or a diagram. This is a DESIGN
          SURFACE, which is none of the three, and it is here because Erez asked
          for it in these words. The field spec behind it lives where it should
          — product chapter 12 §13m — and this file cites it rather than
          restating it, so the notebook stays the bible.

          The components are the dashboard's real shadcn/ui set (new-york,
          neutral, lucide) already installed at src/components/ui — no new
          dependency, no second design system, and Atlas builds against the same
          primitives he can import.

          ⚠ It is INTERACTIVE, not a picture: the five complexity dropdowns
          really compute the rating, and the six-population table really
          re-prices the header. That is the point — a static mock cannot show
          that five dropdowns move fifty downstream numbers. */}
      <Sec n={'The input forms'}>
      {/* ⛔ THE shadcn MOCKS ARE GONE, ON EREZ'S WORD (2 Sept): "take these two
          pages from Quinn's board and bring it to your board — just replace the
          input forms designed in shadcn/ui with Quinn's documents, because
          they're more up-to-date."

          They were hand-built React that LOOKED like the screen. These are the
          designs themselves — the same two `page_content` rows Quinn presents
          at the top of /outform-board, derived field-by-field from the 714
          family. Same component, same frame size and same order as hers, so the
          two boards cannot drift into showing different things under one name.

          ⭐ AND THIS IS THE BOARD LAW WORKING RATHER THAN BEING BENT: a board
          section is a KB page PRESENTED. A mock is a fourth thing — it says what
          we intend and nothing keeps it true. `26` and `29` update themselves
          the moment either page changes. */}
      <SectionBox title="The two input pages — the designs, from the 714 family" layout="custom">
        <div style={{ display: "flex", gap: 60, alignItems: "flex-start" }}>
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/26-detailed-input-page"
            label="JOURNEY B · Every input on the detailed estimate, derived field-by-field from 264714 and its two siblings — their labels, their vocabulary, the calculation chain behind each"
            width={1180}
            height={1560}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/29-ballpark-input-page"
            label="JOURNEY A · The ballpark, with the historical comparables between the job and the component list — six states, the seven weights behind the score, and the four gaps the code still has"
            width={1180}
            height={1560}
          />
          {/* Erez, 2 Sept: "create a copy of this page and give another option
              for the UI, and just put it next to it in the section." Beside
              Quinn's, not replacing it — two options are read against each
              other or they are not options. It also carries the Data Pilot
              indicator audit he asked for in the same breath. */}
          <DocumentFrame
            slug="kb-doc/consultancy/outform/product/30-ballpark-input-option-b"
            label="OPTION B · The split ledger — the cost column sticky beside the inputs so the money never scrolls away, plus every Data Pilot indicator checked against our code"
            width={1180}
            height={1560}
          />
        </div>
      </SectionBox>
      </Sec>
      {/* ══ THE PRODUCT + ITS COVERAGE CHECKLIST, ONE SECTION ════════════════
          Erez, 1 Sept: "I want to see it on the board in the same section as
          the product page." Two frames side by side, same gray box — the
          document, and the instrument that says whether the document is
          finished. Reading them apart is how a coverage claim goes unchecked:
          the first measurement said 51% and was a bug in the grader, not a gap. */}
      <Sec n={'The product'}>
      <SectionBox title="The product" layout="custom">
        <div style={{ display: "flex", gap: 40, alignItems: "flex-start" }}>
          <div>
            <div style={DOCLBL}>The product — a notebook of chapters</div>
            <NotebookFrame
              groups={[
                { label: "Product", prefix: "kb-doc/consultancy/outform/product/" },
                { label: "Architecture", prefix: "kb-doc/consultancy/outform/architecture/" },
              ]}
              label="The product notebook — Product and Architecture, each collapsible"
              width={1400} height={1500} />
          </div>
          {/* Erez, 2 Sept: "the strategy page, let's bring to the product
              section between the notebook and the blueprint checklist." It had
              a section of its own two rows up; it reads better here, against
              the thing it is a strategy FOR. */}
          <div>
            <div style={DOCLBL}>The strategy</div>
            <DocumentFrame slug="kb-doc/consultancy/outform/the-strategy" label="The system is largely built and wired to nothing human — three moves, and the ledger of eight cycles" width={1100} height={1500} />
          </div>
          <div>
            <div style={DOCLBL}>✅ Blueprint checklist — 468 requirements</div>
            <DocumentFrame slug="kb-doc/consultancy/outform/blueprint-checklist" label="Every requirement in their Rev 10 blueprint, grouped into 49 rows with its ID range — 394 answered, 74 deferred with the reason, 0 gaps" width={1100} height={1500} />
          </div>
          <div>
            <div style={DOCLBL}>✅ Prototype checklist — nine tabs</div>
            <DocumentFrame slug="kb-doc/consultancy/outform/prototype-checklist" label="Their v4.1 HTML walked tab by tab, every mechanism graded REAL or FABRICATED — five real ones we did not have, all computable from the cost model" width={1100} height={1500} />
          </div>
          {/* THE THIRD CHECKLIST, and it is the newest source we have. The
              blueprint says what they want; the prototype shows what they mean;
              this is Zaid and Scott explaining what they actually DO, out loud,
              while pointing at the cells. Where it disagrees with either of the
              other two, it wins — it is four months newer than the prototype and
              it describes the system running today.
              ⛔ Its rule is the other two's rule: a box is green only when the
              learning is written into a chapter that EXISTS. Understanding
              something is not addressing it — they explained ACM perfectly on
              1 Sept and twenty-six chapters still said nothing about it. */}
          <div>
            <div style={DOCLBL}>🎙️ Estimator meeting checklist — 39 boxes, 5 green</div>
            <DocumentFrame slug="kb-doc/consultancy/outform/walkthrough-checklist" label="41 minutes with Zaid and Scott walking their live workbook — ACM defined, the breakout column map, the locked sales sheet, and eight things that change what we build" width={1100} height={1500} />
          </div>
          {/* ⛔ QUESTIONS FOR ZAID IS NOT A FRAME HERE, DELIBERATELY. It is
              chapter 26 INSIDE the notebook (Erez, 1 Sept: "questions for Zaid
              needs to be inside the product notebook"), so the NotebookFrame to
              the left already carries it. A second frame would be the same page
              in two places, and then one of them goes stale.
              THREE CHECKLISTS SIT HERE, and that is the whole set: what they
              WROTE (blueprint), what they BUILT (prototype), what they SAID
              (the estimator meeting). */}
        </div>
      </SectionBox>
      </Sec>
      {/* ══ OUTFORM — THE SOURCE DOCUMENTS, ONE SECTION ══════════════════════
          Erez, 31 Aug: "all the documents that I asked you, put them in one
          section." They were five boxes; five gray containers for five frames
          is four containers too many, and it broke the row. One section, five
          frames side by side, in the order he weighted them. */}
      <Sec n={'The source documents'}>
      <SectionBox title="The source documents" layout="custom">
        <div style={{ display: "flex", gap: 40, alignItems: "flex-start" }}>
          <div>
            <div style={DOCLBL}>Their prototype — v4.1, live</div>
            <iframe
              src="/dash/outform/estimate-template-v4_1.html"
              title="Outform Estimate Template v4.1"
              className="nopan nodrag"
              style={{ width: 1180, height: 1400, border: "1px solid #DEDEDE", borderRadius: 10, background: "#fff" }}
            />
          </div>
          {/* ⛔ THIS SECTION IS THEIRS ONLY (Erez, 1 Sept): "the idea is that
              this section called the source documents basically have all the
              source documents. And when I tell you, look at the source
              documents, you just go and read this stuff and it's all their
              stuff."
              REMOVED in that pass, and why:
              · 261152 L'Oréal — the job summary is superseded
              · prototype-as-spec — the prototype checklist covers it, and that
                lives in the product notebook now
              · where-the-logic-lives — the notebook covers it
              The last two were OURS, not theirs, which is the whole reason they
              do not belong in a section named "the source documents". */}
          {/* ⛔ THE BLUEPRINT IS THEIR PDF, NOT OUR KB PAGE — and I nearly put
              the wrong one here. Erez asked the right question: is the KB page
              one-to-one? MEASURED, and it is not:
                · 0 of 468 requirement IDs appear in it
                · 2 of 40 sampled requirement texts appear verbatim
              91,765 characters of genuine work, and it is our READING of their
              blueprint, not a transcription of it. Calling it "our rendering"
              was already too generous. So the source-documents section gets
              their actual 24-page PDF, same artefact the Outform board carries;
              our analysis stays where analysis belongs.
              ⭐ THE RULE THIS SETTLES: a section that promises "all their stuff"
              earns it by being CHECKED, not by being labelled. */}
          <PresentationFrame
            slidesBase={`${BLUEPRINT_R2}/slides`}
            slideCount={24}
            pdf={`${BLUEPRINT_R2}/outform-ai-estimating-blueprint-rev10.pdf`}
            label="Their blueprint · AI Estimating Tool · Architecture Blueprint Rev 10 · Apr 2026 · their document, unedited"
            width={880}
            aspect={1275 / 1650}
            caption="24 pages, ten internal drafts deep, with a COO as executive sponsor and a CIO as technical sponsor. Four engines, ~1,500 past jobs, four-tier RBAC, D365 · Sugar · Data Lake · Smartsheet. 468 requirements extracted to docs/blueprint-requirements.json. ⚠️ Newer than this: their v4.1 prototype, which carries Finance feedback the blueprint never saw — where the two disagree, the prototype wins."
          />
          <div>
            <div style={DOCLBL}>⛔ Change summary — what is fabricated</div>
            <DocumentFrame slug="kb-doc/consultancy/outform/change-summary-v4" label="Read section 4 before designing from the prototype" width={880} height={1400} />
          </div>
          <div>
            <div style={DOCLBL}>The competitor — Data Pilot</div>
            <DocumentFrame slug="kb-doc/consultancy/outform/competitor-demo" label="Three ways in, a six-step ballpark wizard, a seven-step detailed wizard" width={880} height={1400} />
          </div>
          {/* ⭐ THE 714 FAMILY — FOUR WORKBOOKS, THE ORIGINAL FILES.
              Erez asked Zaid for more of this template and he sent three; with
              264714 itself that is the family. They are served AS THE ORIGINAL
              .xlsx, deliberately: "don't change it because you need to keep
              referring to it and analyzing it. It needs to be the original
              document." Verified byte-identical to what he dropped.
              ⛔ Their newest generation, and the only one that matters for
              business logic — Scott: "how we function today is all in this new
              sheet. Period." Every job since May goes through it, 500–600 of
              them. Our corpus holds ten of the OLD method and one of this. */}
          <div>
            <div style={DOCLBL}>⭐ The 714 family — four original workbooks</div>
            <div style={{ width: 880, background: "#fff", border: "1px solid #DEDEDE", borderRadius: 10, padding: 22 }}>
              <div style={{ fontSize: 13, lineHeight: 1.55, color: "#4a5158", marginBottom: 16 }}>
                Their current methodology, unmodified. <b>264714 is the one we decoded</b>; the other three
                came from Zaid on 1 September when Erez asked for more of the same template.
              </div>
              {[
                ["264714 — Morphe, Sephora 2-Bay, Oct 2026", "264714-morphe-sephora-2bay.xlsx", "the anchor — reproduced to 4dp"],
                ["251124 — Haus Labs, Sephora Overhaul 2026 R2", "251124-haus-labs-sephora-overhaul.xlsx", "workbook + breakout"],
                ["261570B — Rhode, NA Holiday 26, K1", "261570B-rhode-na-holiday-26.xlsx", "estimate tool"],
                ["Estimating workbook — updated 26 Aug 2026", "estimating-workbook-updated-2026-08-26.xlsx", "the template itself, latest"],
              ].map(([label, file, note]) => (
                <a
                  key={file}
                  href={`/dash/outform/${file}`}
                  className="nopan nodrag"
                  style={{
                    display: "block", padding: "11px 0", borderBottom: "1px solid #F0F1F3",
                    textDecoration: "none", color: "#14181d",
                  }}
                >
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: 12, color: "#8b949f" }}>{note} · .xlsx</div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </SectionBox>
      </Sec>
      {/* ══ THE DATABASE WE ALREADY BUILT ══════════════════════════════════════
          Erez, 31 Aug: "bring also the entity diagrams that we created and other
          things that we have about the database."
          These already existed in board/command/ — eight per-job entity diagrams,
          the introspected ERD, the workbook anatomy — and every one of them was
          only reachable from Quinn's board. They are the evidence base for the
          ARCHITECTURE document, so they belong beside the source documents that
          feed the PRD. Imported, not copied: one definition, two boards. */}
      <Sec n={'The database today'}>
      <SectionBox title="The database today" layout="custom">
        <div style={{ display: "flex", gap: 40, alignItems: "flex-start" }}>
          <div>
            <div style={DOCLBL}>51 tables, 46 foreign keys — introspected, not drawn</div>
            <OutformErd />
          </div>
          <div>
            <div style={DOCLBL}>The programme — how data moves</div>
            <DataProgrammeDiagram />
          </div>
        </div>
      </SectionBox>
      </Sec>
      <Sec n={'The entity diagrams'}>
      <SectionBox title="The entity diagrams" layout="custom">
        <div style={{ display: "flex", gap: 40, alignItems: "flex-start" }}>
          <div>
            <div style={DOCLBL}>261152 L'Oréal — the entities, for the job they sent</div>
            <Entity261152 />
          </div>
          <div>
            <div style={DOCLBL}>261152 — the job's own anatomy</div>
            <Job261152Diagram />
          </div>
          <div>
            <div style={DOCLBL}>What a workbook is made of</div>
            <OutformWorkbookAnatomy />
          </div>
          <div>
            <div style={DOCLBL}>All eleven jobs, at a glance</div>
            <OutformElevenDiagram />
          </div>
        </div>
      </SectionBox>
      </Sec>
      <Sec n={'Brief for Scott'}>
      <SectionBox title="Brief for Scott" layout="custom">
        <DocumentFrame
          slug="kb-doc/consultancy/outform/the-eleven-for-scott"
          label="The eleven jobs, four engines, the questions for Scott, and what one form must hold"
          width={900}
          height={1400}
        />
      </SectionBox>
      </Sec>
      {/* ⭐ THE ARCHITECTURE, as a DIAGRAM — Erez, 30 Aug 2026: "can you create it
          as an architecture diagram?" after the written version.
          Data-flow archetype per skill://diagrams: the question is "what happens
          to one cell, in order", so the numbered discs ARE the diagram. Built on
          the house engine (command/c4.tsx) — nothing hand-placed.
          THE ARGUMENT: the engine is the only red box because arithmetic happens
          in exactly ONE layer. Left of it is import, right of it is display, and
          a view is handed finished values so it cannot derive a business number.
          That is what makes it safe to let an agent write the page. */}
      <Sec n={'The system diagram'}>
      <SectionBox title="The system diagram" layout="custom">
        <OutformArchitectureDiagram />
      </SectionBox>
      </Sec>
      {/* ⭐ TOP SECTION, at Erez's instruction 30 Aug 2026 — "I need to see an
          architecture diagram of our code immediately. I don't understand
          what's going on... create a knowledge base page with this at the top
          of your board."
          SIX LAYERS, ONE RULE: arithmetic happens in exactly one layer. Above
          the engine is import; below it is display. The page carries the sizes
          counted that day (engine ~2,000 lines, 4 fact tables, ~15 business
          columns) and the two things the layering makes obvious — that layer 5
          has two occupants building DIFFERENT artefacts (views.py mirrors their
          WORKBOOK; their prototype is their PRODUCT, and not one of its nine
          tabs is a sheet name), and that everything forward-looking is empty.
          A KB page PRESENTED, per the board law. Editing the page updates this
          box with no deploy. Nothing was removed — the board is cumulative. */}
      <Sec n={'The code architecture'}>
      <SectionBox title="The code architecture" layout="custom">
        <DocumentFrame
          slug="kb-doc/consultancy/outform/the-architecture-in-one-page"
          label="Six layers, one rule — arithmetic lives in exactly one of them"
          width={1040}
          height={1180}
        />
      </SectionBox>
      </Sec>
      {/* ⭐ FIRST, at Erez's instruction 29 Aug 2026 — "put it on your board as
          the first, as the top section, because I don't see it."
          THE ARGUMENT: Outform is legacy application modernization, not a data
          migration. He asked to be taught the data field from zero, so the page
          decomposes ONE real cell from job 261801 into the five places a real
          system splits it across, then carries the external evidence (Cunha &
          Saraiva on spreadsheet reverse engineering; Feathers on characterization
          tests; Databricks' own "formulas are ingested as their computed values").
          It is a KB page PRESENTED — per the board law, if you want to say
          something you write it in the KB and show it here. Editing the page
          updates this box with no deploy. The Room moves to second; nothing is
          removed, because the board is cumulative. */}
      <Sec n={'Where logic lives'}>
      <SectionBox title="Where logic lives" layout="custom">
        <DocumentFrame
          slug="kb-doc/consultancy/outform/where-the-logic-lives"
          label="One cell, five destinations — why this is not a data migration"
          width={1040}
          height={1180}
        />
      </SectionBox>
      </Sec>
      {/* THE ROOM — was the top section from 28 Aug until 29 Aug, when Erez put
          the argument above it. Added 28 Aug
          2026 during the Outform data push — the one surface where Jarvis,
          Atlas and Quinn write to EACH OTHER while he reads it live. Everything
          else on this board is single-author; this is not. It polls itself, so
          it moves in front of him without a refresh and without a socket.
          Writes: .claude/scripts/room.sh <Author> "…". See sql/058. */}
      <Sec n={'The agent room'}>
      <SectionBox title="The agent room" layout="custom">
        <AgentRoom />
      </SectionBox>
      </Sec>
      {/* Row 1 — the 3 planning views define the board width */}
      <Sec n={'Three planning views'}>
      <PlanningVariations />
      </Sec>
      {/* Row 2 — in order: Memory · Skills · YouTube · Projects · Video Lab */}
      {/* Memory — agent-centric brain: sidebar of agents → summary/project/logs + company brain */}
      {/* Under the brain app, the HYDRATION diagram (Erez, 9 Aug): the same brain
          seen from the READER's side — which of these rows each agent's CLAUDE.md
          actually loads on wake, and the five platform documents no contract
          names at all. It belongs in THIS box rather than a new one: the app is
          the store, the diagram is who reads it, and the argument only lands
          when the two adjoin. */}
      <Sec n={'The agent brain'}>
      <SectionBox title="The agent brain" layout="custom">
        <div style={{ display: "flex", flexDirection: "column", gap: 40, alignItems: "flex-start" }}>
          <AgentBrainApp />
          <HydrationDiagram />
        </div>
      </SectionBox>
      </Sec>
      {/* Skills — the living law, browsable: sidebar + filters → gallery → detail */}
      <Sec n={'The living skills'}>
      <SectionBox title="The living skills" layout="custom">
        <SkillsApp />
      </SectionBox>
      </Sec>
      {/* Open Source — the directory, sibling of Skills: every project carries a verdict */}
      <Sec n={'Open source directory'}>
      <SectionBox title="Open source directory" layout="custom">
        <OpenSourceApp />
      </SectionBox>
      </Sec>
      {/* Courses — sibling of Open Source: every course carries a stage + a takeaway */}
      <Sec n={'The course library'}>
      <SectionBox title="The course library" layout="custom">
        <CoursesApp />
      </SectionBox>
      </Sec>
      {/* Tools — commercial products we evaluated; closed software has no home
          in the open-source directory, so it used to evaporate */}
      <Sec n={'Commercial tools reviewed'}>
      <SectionBox title="Commercial tools reviewed" layout="custom">
        <ToolsApp />
      </SectionBox>
      </Sec>
      {/* Leads — the pipeline as one funnel, live from pipeline_deals (replaced the
          static Business board, which had drifted: Falcon, Air Duct and Plus500
          were live engagements that never appeared on it). */}
      <Sec n={'The lead pipeline'}>
      <SectionBox title="The lead pipeline" layout="custom">
        <LeadsKanban />
      </SectionBox>
      </Sec>
      {/* Anime.js Lab — Erez sent animejs.com and asked what we can do with it.
          Ten RUNNING demos rather than a feature list, each captioned with the
          place we would actually use it. The library was already a dependency
          (4.5.0 resolved, not the ^4.4.1 pinned) and was used nowhere. */}
      <Sec n={'The animation lab'}>
      <SectionBox title="The animation lab" layout="custom">
        <AnimeLab />
      </SectionBox>
      </Sec>
      {/* YouTube — the watched-video knowledge app (moved here from the Knowledge board) */}
      <Sec n={"Everything we watch"}>
      <SectionBox title="Everything we watch" layout="custom">
        <KnowledgeFeedBoard />
      </SectionBox>
      </Sec>
      <Sec n={'The project kanban'}>
      <Projects projects={projects} plans={cs.agent_plans ?? []} decisions={cs.decisions ?? []} />
      </Sec>
      {/* Matan Partnership — added 24 Aug 2026, corrected twice the same day.
          Pass 1 was a static KB page; pass 2 was a styled React calculator
          (JS doing the math behind blue-number inputs) — Erez rejected both:
          "copy that Univer, Google-like sheet, and make it inside there." He
          meant literally the same engine Outform's board proves
          (board/outform/univer-sheet.tsx) — real Univer cells with a real
          formula engine, not a picture of one. MatanUniverSheet is that: a
          local (no client file to load) workbook built in
          company/matan-univer.tsx, deal log feeding a quarterly rollup via
          genuine =SUMIF/=IF formulas. The written recommendation stays
          underneath as a presented KB page. */}
      <Sec n={'Matan partnership forecast'}>
      <SectionBox title="Matan partnership forecast" layout="custom">
        <div style={{ display: "flex", flexDirection: "column", gap: 32, alignItems: "flex-start" }}>
          <Suspense fallback={<div style={{ width: 1200, height: 560 }} />}>
            <MatanUniverSheet />
          </Suspense>
          <DocumentFrame slug="kb-doc/company/matan-partnership-model"
            label="The full write-up — three structures and the recommendation" width={960} height={1500} />
        </div>
      </SectionBox>
      </Sec>
      <Sec n={'The video lab'}>
      <VideoLab onPlay={onPlay} />
      </Sec>

      {/* ── BOTTOM ROW — the two live leads, priced by ONE instrument ──────────
          Four surfaces, deliberately adjacent so the rate can be compared at a
          glance: each client's calculator (the what-if machine) beside the
          proposal that machine produces.

          The calculators are IMPORTED from Ruby's and Hank's board folders, not
          copied. A copy would drift the moment either of them tuned a knob, and
          the whole point of putting these side by side is that the rate card is
          provably identical across both clients. One rate; only scope moves. */}
      <Sec n={'One rate card'}>
      <SectionBox title="One rate card" layout="custom">
        <div style={{ display: "flex", gap: 40, alignItems: "flex-start" }}>
          <AirDuctPricingSheet />
          <Everything420PricingSheet />
        </div>
      </SectionBox>
      </Sec>
      {/* Three ways to draw the same system — Erez picks one, then it becomes
          the third pattern in skill://diagrams alongside LANES and BLUEPRINT. */}
      {/* THE MOVE — added 2026-08-11, the night the desk first came up on the
          Hetzner box. It answers one question: if we move right now, what
          changes? The fourth column (client boxes) is the whole argument —
          client work has never happened on the Mac, so moving the agents
          changes where they SSH FROM and nothing else. Red is spent only on
          the three things genuinely unfinished. */}
      <Sec n={'Local versus server'}>
      <SectionBox title="Local versus server" layout="custom">
        <TheMoveDiagram />
      </SectionBox>
      </Sec>
      <Sec n={'The diagram standard'}>
      <SectionBox title="The diagram standard" layout="custom">
        <C4Diagram />
      </SectionBox>
      </Sec>
      <Sec n={'The work proposals'}>
      <SectionBox title="The work proposals" layout="custom">
        <div style={{ display: "flex", gap: 40, alignItems: "flex-start" }}>
          <AirDuctPhaseOneProposal />
          <AirDuctProposal />
          <Everything420Proposal />
        </div>
      </SectionBox>
      </Sec>
      {/* OUTFORM moved off this board 7 Aug 2026. Quinn's /outform-board now
          opens with "Their blueprint" — the same 24 slides, same PDF, same R2
          path, with a fuller caption. Keeping a second copy here would have
          drifted the moment she edited hers, and this board is not where a
          client's own document belongs once that client has a board.
          Nothing was lost: the section moved, it was not deleted. */}
    </SectionGrid>
    </VisCtx.Provider>
  );
}
function MenuNode({ data }: { data: { vis: Set<string>; setVis: (s: Set<string>) => void } }) {
  return <SectionMenu vis={data.vis} setVis={data.setVis} />;
}
// Erez, 31 Aug: "put the menu outside of this grid — don't move the section,
// keep the section where it is." As a flex child it was a layout participant
// and shoved every section right. Its own node sits at a negative x, so the
// grid's geometry is untouched and the menu still zooms with the canvas.
const nodeTypes: NodeTypes = {
  grid: GridNode as unknown as NodeTypes[string],
  menu: MenuNode as unknown as NodeTypes[string],
};

export function CommandBoardPage() {
  const api = useApi();
  const [cs, setCs] = useState<CommandState | null>(null);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playKey, setPlayKey] = useState<string | null>(null);
  const [vis, setVis] = useState<Set<string>>(() => new Set(DEFAULT_ON));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          api("/api/command-state"),
          api("/api/projects"),
        ]);
        if (!r1.ok) throw new Error(`command-state ${r1.status}`);
        if (!r2.ok) throw new Error(`projects ${r2.status}`);
        const state = (await r1.json()) as CommandState;
        const projBody = await r2.json();
        const projRows: Project[] = Array.isArray(projBody) ? projBody : (projBody.projects ?? projBody.rows ?? []);
        if (!cancelled) {
          setCs(state);
          const live = projRows.filter((p) => p.status !== "archived").sort((a, b) => (a.board_order ?? 999) - (b.board_order ?? 999));
          setProjects([JARVIS_PROJECT, ...live]);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nodes = useMemo<Node[]>(() => {
    if (!cs || !projects) return [];
    return [
      { id: "menu", type: "menu", position: { x: -640, y: 0 }, draggable: false, data: { vis, setVis } },
      { id: "grid", type: "grid", position: { x: 0, y: 0 }, draggable: false, data: { cs, projects, onPlay: setPlayKey, vis, setVis } },
    ];
  }, [cs, projects, vis]);

  if (error) return <div style={{ padding: 40, fontFamily: FONT, color: T.red }}>Jarvis board failed to load: {error}</div>;
  if (!cs || !projects) {
    return <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: T.faint }}><Loader2 style={{ width: 22, height: 22, animation: "spin 1s linear infinite" }} /></div>;
  }
  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <BoardCanvas nodes={nodes} nodeTypes={nodeTypes} fitViewPadding={0.12} />
      {playKey && <VideoModal playKey={playKey} onClose={() => setPlayKey(null)} />}
    </div>
  );
}
